import createDOMPurify from 'dompurify';
import { marked } from 'marked';
import type { CommentAttachment } from '@tourfold/libraries/tf-api-client-public-v2';
import { type InlineImageSize, parseSizeFromAlt } from './inline-image-size';

/**
 * Comments-specific markdown renderer — the only entrypoint every comments-rendering surface
 * uses to turn `body_markdown` into safe HTML for `[innerHTML]`.
 *
 * The app follows a feature-local sanitizer pattern: each feature that renders markdown
 * (AI assistant, comments, …) owns its own renderer module + allow-list. We deliberately avoid
 * a global `provideMarkdown(...)` config because every consumer of `<markdown>` would inherit
 * the same allow-list — which means one feature's lenience (or strictness) silently bleeds into
 * unrelated surfaces. Owning the pipeline per feature makes the XSS surface explicit and
 * documented in one file.
 *
 * Allow-list rationale:
 *  - Text formatting (`strong`, `em`, `del`, `code`, `pre`, headings 1–3, lists, blockquotes,
 *    `br`) mirrors the backend's markdown allow-list (`comments.body-invalid-markdown` rejects
 *    anything outside this set on submit).
 *  - `a` is allowed with the link hook below restricting `href` to http(s) / mailto.
 *  - `img` is allowed with `src` restricted to:
 *      * `http(s):` — the resolved presigned URLs the server emits on the read path.
 *      * `attachment:` — the canonical token form that survives in Tiptap editor state and on
 *        the wire when the user is composing or editing.
 *    Everything else (data-URI, javascript:, file:, etc.) is dropped — XSS surface.
 *  - `class` is forbidden so a malicious paste of `<img class="…">` can't smuggle styling onto
 *    the chip / image. Same for `style`, `id`, on*-attrs (DOMPurify drops on* by default).
 *  - SVG / iframe / object / embed / picture / audio / video stay forbidden — comments aren't
 *    allowed to host arbitrary media.
 */

const ALLOWED_TAGS = [
  'a',
  'blockquote',
  'br',
  'code',
  'del',
  'em',
  'h1',
  'h2',
  'h3',
  'hr',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'strong',
  'ul',
];

const ALLOWED_ATTR = ['alt', 'href', 'rel', 'role', 'src', 'tabindex', 'target', 'title'];

const FORBID_ATTR = ['class', 'id', 'style', 'srcset'];

const FORBID_TAGS = [
  'audio',
  'embed',
  'iframe',
  'math',
  'object',
  'picture',
  'source',
  'svg',
  'video',
];

let purifier: ReturnType<typeof createDOMPurify> | null = null;
let hooksRegistered = false;

function getPurifier(): ReturnType<typeof createDOMPurify> {
  purifier ??= createDOMPurify(globalThis.window);
  if (!hooksRegistered) {
    purifier.addHook('afterSanitizeAttributes', (node) => {
      const name = node.nodeName.toLowerCase();
      if (name === 'a') sanitizeAnchor(node);
      else if (name === 'img') sanitizeImage(node);
    });
    hooksRegistered = true;
  }
  return purifier;
}

function sanitizeAnchor(anchor: Element): void {
  const href = anchor.getAttribute('href');
  if (!href || !isAllowedLinkScheme(href)) {
    anchor.removeAttribute('href');
    anchor.removeAttribute('target');
    anchor.removeAttribute('rel');
    return;
  }
  anchor.setAttribute('target', '_blank');
  anchor.setAttribute('rel', 'noopener noreferrer');
}

function sanitizeImage(img: Element): void {
  const src = img.getAttribute('src');
  if (!src || !isAllowedImageScheme(src)) {
    img.removeAttribute('src');
    // No `src` on an `<img>` paints a broken-image glyph in some browsers; the renderer's caller
    // is responsible for hiding such nodes (the click delegation pattern relies on `src` being
    // present, so an attribute-less `<img>` is effectively inert and visually empty).
    return;
  }
  img.setAttribute('loading', 'lazy');
  img.setAttribute('decoding', 'async');
  // Make the image keyboard-activatable so the click→preview affordance works without a mouse.
  // The wrapper's keydown handler filters to Enter/Space and bubbles the same way as click. We
  // mark `role="button"` for screen readers since a focusable image is unusual semantics, and
  // make sure `alt` defaults to an empty string when missing so AT doesn't read the URL.
  img.setAttribute('tabindex', '0');
  img.setAttribute('role', 'button');
  if (!img.hasAttribute('alt')) img.setAttribute('alt', '');
  // The inline `style` for image presentation (checkerboard, sizing, focus ring) is applied
  // *after* DOMPurify finishes — see `applyInlineImageStyles` below. Setting it inside this
  // hook would be wasted: `style` is on `FORBID_ATTR`, and DOMPurify post-processes after
  // hooks, so the attribute would be stripped before reaching the caller.
}

// Theme-token colors resolve against the host element's computed style chain, so the
// checkerboard adapts to light/dark mode. 16px tile = standard "transparency" checker size.
const INLINE_IMG_STYLE = [
  'display: inline-block',
  'max-width: 100%',
  'height: auto',
  'border-radius: 4px',
  'vertical-align: middle',
  'cursor: zoom-in',
  'background-color: var(--p-surface-0)',
  'background-image:' +
    ' linear-gradient(45deg, var(--p-surface-200) 25%, transparent 25%),' +
    ' linear-gradient(-45deg, var(--p-surface-200) 25%, transparent 25%),' +
    ' linear-gradient(45deg, transparent 75%, var(--p-surface-200) 75%),' +
    ' linear-gradient(-45deg, transparent 75%, var(--p-surface-200) 75%)',
  'background-size: 16px 16px',
  'background-position: 0 0, 0 8px, 8px -8px, -8px 0',
].join('; ');

// Mirrors the editor SCSS `.tf-rich-editor__image.tf-img-{s|m|l}` rules so the same image
// renders at the same dimensions in view and edit modes. Appended to {@link INLINE_IMG_STYLE}
// only when the alt suffix carries an explicit size; legacy images (no suffix) fall back to the
// base cap of `max-height: 320px` set by other CSS, matching the pre-feature appearance.
const SIZE_STYLES: Record<InlineImageSize, string> = {
  s: 'max-width: 160px; max-height: 120px',
  m: 'max-width: 320px; max-height: 240px',
  l: 'max-width: 100%; max-height: 480px',
};

function isAllowedLinkScheme(href: string): boolean {
  try {
    const base = globalThis.location?.origin ?? 'http://localhost';
    const url = new URL(href.trim(), base);
    return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'mailto:';
  } catch {
    return false;
  }
}

function isAllowedImageScheme(src: string): boolean {
  const trimmed = src.trim().toLowerCase();
  // `attachment:<uuid>` is the canonical token form Tiptap and the backend both speak. Treat it
  // as a string-prefix check rather than trying to parse it as a URL — the colon scheme isn't
  // registered with the WHATWG URL parser and would otherwise fall through to the catch block.
  if (trimmed.startsWith('attachment:')) return true;
  try {
    const base = globalThis.location?.origin ?? 'http://localhost';
    const url = new URL(src.trim(), base);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Renders a comment's markdown body to safe HTML suitable for binding via `[innerHTML]`.
 *
 * When `attachments` is supplied, every `<img>` in the output is also stamped with a
 * `data-attachment-id="<uuid>"` attribute resolved by matching the rendered `src` (a server-
 * resolved presigned URL — see `CommentAttachmentService.kt` substitution map) against the
 * matching `CommentAttachment.thumbnailUrl` / `.downloadUrl` path. That lets the caller's
 * click handler look up the attachment in O(1) via `target.dataset.attachmentId` instead of
 * re-running the URL match on every click. Edit-history bodies — for which we don't have a
 * point-in-time attachments snapshot — pass no attachments and skip the decoration; their
 * inline images render but aren't clickable, which is acceptable for a read-only history view.
 */
export function renderCommentBody(
  markdown: string,
  attachments: readonly CommentAttachment[] = [],
): string {
  // `marked.parse` is synchronous when `async: false`; the cast lets us treat it as a plain
  // string without an extra `await` plumbing through the caller.
  const html = marked.parse(markdown, { async: false, breaks: true, gfm: true });
  const sanitized = getPurifier().sanitize(html, {
    ALLOW_DATA_ATTR: false,
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR,
    FORBID_TAGS,
  });
  return decorateImages(sanitized, attachments);
}

/**
 * Renders a single inter-mention text run to safe inline HTML.
 *
 * The mention render path in `<tf-comment-row>` splits the body into alternating text + chip
 * segments and keeps the chips as Angular components, so each text run can't go through the
 * block-level {@link renderCommentBody} (which would wrap every run in `<p>` and strip the
 * boundary whitespace around the chips). It still must go through *a* markdown pass, though:
 * the tiptap-markdown serializer encodes a hard line break as a trailing backslash (`\` + `\n`)
 * and escapes markdown punctuation (`\*`, `\_`, …). Interpolating the raw run as plain text
 * leaks those backslashes into the rendered comment — the reported "newlines get a backslash"
 * bug.
 *
 * `marked.parseInline` resolves exactly those inline constructs — `\`+newline → `<br>`, escaped
 * punctuation back to literals, plus inline emphasis / code / links — without emitting any
 * block wrapper. The result is sanitized through the same allow-list as the block path. Block
 * markdown (lists, headings, blockquotes, fenced code) inside a mention body stays unprocessed,
 * matching the pre-fix behaviour of this path.
 */
export function renderCommentInlineText(markdown: string): string {
  const html = marked.parseInline(markdown, { async: false, breaks: true, gfm: true });
  return getPurifier().sanitize(html, {
    ALLOW_DATA_ATTR: false,
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR,
    FORBID_TAGS,
  });
}

/**
 * Re-parses the sanitized HTML in a detached div and decorates every `<img>` element with:
 *  - an inline `style` attribute (checkerboard + size caps + cursor + focus ring), and
 *  - a `data-attachment-id` attribute matched from the attachments list when supplied.
 *
 * Both attributes are post-sanitization additions because DOMPurify strips `style` (it's on
 * `FORBID_ATTR`) and strips `data-*` (we set `ALLOW_DATA_ATTR: false`). Doing it after the
 * sanitizer finishes is the boring path that works under every CSS-encapsulation mode + Angular
 * `DomSanitizer` policy (the caller wraps the output in `bypassSecurityTrustHtml`).
 *
 * The style value is a fixed constant; the `data-attachment-id` is a backend-issued UUID we
 * already trust. No user-derived content reaches either attribute, so there's no XSS surface
 * added by skipping DOMPurify for this last step.
 */
function decorateImages(html: string, attachments: readonly CommentAttachment[]): string {
  if (!html.includes('<img')) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  const idByPath = attachments.length > 0 ? buildPathToIdMap(attachments) : null;
  for (const img of Array.from(container.querySelectorAll('img'))) {
    // Parse the alt suffix for the size marker the editor wrote on submit. Strip the suffix
    // from the displayed alt so screen readers hear the clean filename, then append the
    // matching max-width / max-height caps to the inline style so the rendered image visually
    // matches the editor's choice. Legacy images (no suffix) fall through to the base style.
    const altRaw = img.getAttribute('alt');
    const { displayAlt, size } = parseSizeFromAlt(altRaw);
    if (size) {
      img.setAttribute('alt', displayAlt);
      img.setAttribute('style', `${INLINE_IMG_STYLE}; ${SIZE_STYLES[size]}`);
    } else {
      img.setAttribute('style', INLINE_IMG_STYLE);
    }
    if (!idByPath) continue;
    const id = resolveAttachmentId(img.getAttribute('src'), idByPath);
    if (id) img.dataset['attachmentId'] = id;
  }
  return container.innerHTML;
}

/**
 * Indexes the comment's inline attachments by URL pathname. The backend's body-substitution
 * step prefers `thumbnailUrl` over `downloadUrl` (smaller, faster first paint), so we MUST
 * map both pathnames to the same attachment id — otherwise an inline image whose body src is
 * the thumbnail URL wouldn't resolve.
 */
function buildPathToIdMap(attachments: readonly CommentAttachment[]): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  for (const att of attachments) {
    if (!att.isInline) continue;
    if (att.thumbnailUrl) {
      const p = pathOfUrl(att.thumbnailUrl);
      if (p) out.set(p, att.filestoreFileId);
    }
    if (att.downloadUrl) {
      const p = pathOfUrl(att.downloadUrl);
      if (p) out.set(p, att.filestoreFileId);
    }
  }
  return out;
}

function resolveAttachmentId(
  src: string | null,
  idByPath: ReadonlyMap<string, string>,
): string | null {
  if (!src) return null;
  // Fast path for the canonical token form (used by Tiptap editor state + edit-form seeds).
  const TOKEN = 'attachment:';
  if (src.startsWith(TOKEN)) return src.slice(TOKEN.length).toLowerCase();
  const path = pathOfUrl(src);
  return path ? idByPath.get(path) ?? null : null;
}

function pathOfUrl(url: string): string | null {
  try {
    const base = globalThis.location?.origin ?? 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    return null;
  }
}
